/**
 * oct20a S1 — voluntary diagnostic evidence ("Findings"), the same on phone, tablet and desktop.
 *
 * A technician working a diagnosis (running, paused, or after their session while the diagnosis is open)
 * may record what they tested, the result, what they read, their reasoning, what they found and what is
 * still unresolved. Each save is a NEW entry with who / when / which session — nothing is edited or
 * overwritten, and nothing here is the diagnostic outcome: the outcome is recorded once, when the
 * diagnostic work ends (Stop). Phone-sized controls (≥44px) below the md breakpoint.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Stethoscope, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import {
  CONFIDENCE_LABELS, DIAGNOSTIC_COMPETENCY_LABELS, type DiagnosticConfidence,
} from '@/types/diagnostic';
import {
  EVIDENCE_PHASE_LABELS, evidencePayload, evidenceProblem, evidenceRefusalMessage,
  type EvidenceDraft, type EvidenceTestDraft,
} from '@/lib/diagnostics/evidence';
import {
  readDiagnosticEvidenceTimeline, recordMyDiagnosticEvidence,
  type DiagnosticEvidenceTimeline, type EvidenceEntry,
} from '@/lib/diagnostics/evidenceApi';

/** ≥44px tap targets on phones; the desktop density below md. */
export const PHONE_TAP = 'min-h-11 md:min-h-9';

const EMPTY_TEST: EvidenceTestDraft = { testName: '', result: '', measurement: '' };

export interface DiagnosticEvidenceFormProps {
  jobs: ReadonlyArray<{ jobId: string; jobName: string }>;
  busy: boolean;
  onSubmit: (jobId: string, draft: EvidenceDraft) => void;
}

/** The entry form. Presentational: the caller records it. */
export function DiagnosticEvidenceForm({ jobs, busy, onSubmit }: DiagnosticEvidenceFormProps) {
  const [jobId, setJobId] = useState<string | null>(jobs.length === 1 ? jobs[0].jobId : null);
  const [draft, setDraft] = useState<EvidenceDraft>({ tests: [{ ...EMPTY_TEST }] });
  useEffect(() => {
    if (jobs.length === 1) setJobId(jobs[0].jobId);
  }, [jobs]);
  const problem = jobId === null ? 'Choose the diagnostic job.' : evidenceProblem(draft);
  const setTest = (i: number, patch: Partial<EvidenceTestDraft>) =>
    setDraft((d) => ({ ...d, tests: (d.tests ?? []).map((t, k) => (k === i ? { ...t, ...patch } : t)) }));

  return (
    <form
      className="space-y-3"
      data-testid="diagnostic-evidence-form"
      onSubmit={(e) => {
        e.preventDefault();
        if (problem || jobId === null) return;
        onSubmit(jobId, draft);
      }}
    >
      {jobs.length > 1 && (
        <div className="space-y-1">
          <Label>Diagnostic job</Label>
          <div className="grid gap-1" role="radiogroup" aria-label="Diagnostic job">
            {jobs.map((j) => (
              <button key={j.jobId} type="button" role="radio" aria-checked={jobId === j.jobId} disabled={busy}
                      data-testid={`evidence-job-${j.jobId}`} onClick={() => setJobId(j.jobId)}
                      className={cn(PHONE_TAP, 'rounded-md border px-3 py-2 text-left text-sm',
                        jobId === j.jobId ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted')}>
                {j.jobName}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label>Tests performed</Label>
        {(draft.tests ?? []).map((t, i) => (
          <div key={i} className="grid gap-1 rounded-md border p-2" data-testid={`evidence-test-${i}`}>
            <Input aria-label="Test" placeholder="Test (e.g. fuel rail pressure, KOEO)" value={t.testName}
                   className={PHONE_TAP} disabled={busy} onChange={(e) => setTest(i, { testName: e.target.value })} />
            <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
              <Input aria-label="Result" placeholder="Result (e.g. low)" value={t.result}
                     className={PHONE_TAP} disabled={busy} onChange={(e) => setTest(i, { result: e.target.value })} />
              <Input aria-label="What you read" placeholder="What you read (e.g. 28 psi, spec 55–62)" value={t.measurement}
                     className={PHONE_TAP} disabled={busy} onChange={(e) => setTest(i, { measurement: e.target.value })} />
            </div>
            {(draft.tests ?? []).length > 1 && (
              <Button type="button" variant="ghost" size="sm" className={cn(PHONE_TAP, 'justify-start gap-2')} disabled={busy}
                      onClick={() => setDraft((d) => ({ ...d, tests: (d.tests ?? []).filter((_, k) => k !== i) }))}>
                <Trash2 className="h-4 w-4" /> Remove this test
              </Button>
            )}
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" className={cn(PHONE_TAP, 'gap-2')} disabled={busy}
                data-testid="evidence-add-test"
                onClick={() => setDraft((d) => ({ ...d, tests: [...(d.tests ?? []), { ...EMPTY_TEST }] }))}>
          <Plus className="h-4 w-4" /> Add a test
        </Button>
      </div>

      <div className="space-y-1">
        <Label htmlFor="evidence-findings">What you found</Label>
        <Textarea id="evidence-findings" rows={2} className="min-h-[88px]" disabled={busy} value={draft.findings ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, findings: e.target.value }))} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="evidence-reasoning">Your reasoning</Label>
        <Textarea id="evidence-reasoning" rows={2} className="min-h-[88px]" disabled={busy} value={draft.reasoning ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, reasoning: e.target.value }))} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="evidence-open">Still unresolved</Label>
        <Textarea id="evidence-open" rows={2} className="min-h-[88px]" disabled={busy} value={draft.unresolvedQuestions ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, unresolvedQuestions: e.target.value }))} />
      </div>
      <div className="space-y-1">
        <Label htmlFor="evidence-recommendation">Recommended repair (optional)</Label>
        <Textarea id="evidence-recommendation" rows={2} className="min-h-[88px]" disabled={busy} value={draft.recommendation ?? ''}
                  data-testid="evidence-recommendation"
                  onChange={(e) => setDraft((d) => ({ ...d, recommendation: e.target.value }))} />
        <p className="text-xs text-muted-foreground">Sent to the front office as a recommendation — it does not add or approve a job.</p>
      </div>
      <div className="space-y-1">
        <Label>How sure are you so far? (optional)</Label>
        <div className="flex gap-1" role="radiogroup" aria-label="Confidence so far">
          {([1, 2, 3, 4, 5] as DiagnosticConfidence[]).map((v) => (
            <button key={v} type="button" role="radio" aria-checked={draft.confidence === v} disabled={busy}
                    title={CONFIDENCE_LABELS[v]} data-testid={`evidence-confidence-${v}`}
                    onClick={() => setDraft((d) => ({ ...d, confidence: d.confidence === v ? null : v }))}
                    className={cn(PHONE_TAP, 'min-w-11 flex-1 rounded-md border text-sm',
                      draft.confidence === v ? 'border-primary bg-primary/10 font-medium' : 'hover:bg-muted')}>
              {v}
            </button>
          ))}
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Saved as a new entry with your name and the time. It is not the diagnosis result — that is recorded when the diagnostic work ends.
      </p>
      {problem && <p className="text-xs text-muted-foreground" data-testid="evidence-problem">{problem}</p>}
      <Button type="submit" className={cn(PHONE_TAP, 'w-full')} disabled={busy || problem !== null} data-testid="evidence-save">
        {busy ? 'Saving…' : 'Save findings'}
      </Button>
    </form>
  );
}

/** The repair's diagnostic evidence, oldest first; a session conclusion is labelled as such, never as the result. */
export function DiagnosticEvidenceTimelineList({ timeline }: { timeline: DiagnosticEvidenceTimeline }) {
  if (timeline.entries.length === 0 && timeline.jobs.every((j) => j.diagnosisOutcome === null)) {
    return <p className="text-sm text-muted-foreground" data-testid="evidence-timeline-empty">No findings recorded yet.</p>;
  }
  return (
    <div className="space-y-2" data-testid="evidence-timeline">
      {timeline.jobs.filter((j) => j.diagnosisOutcome !== null).map((j) => (
        <p key={j.jobId} className="rounded-md border border-primary/40 bg-primary/5 p-2 text-sm" data-testid={`evidence-outcome-${j.jobId}`}>
          <span className="font-medium">{j.jobName} — diagnosis result:</span>{' '}
          {DIAGNOSTIC_COMPETENCY_LABELS[j.diagnosisOutcome!]}
        </p>
      ))}
      {timeline.entries.map((e) => <EvidenceEntryCard key={e.entryId} e={e} />)}
    </div>
  );
}

function EvidenceEntryCard({ e }: { e: EvidenceEntry }) {
  const when = new Date(e.recordedAt);
  return (
    <div className="rounded-md border p-2 text-sm" data-testid={`evidence-entry-${e.entryId}`}>
      <p className="text-xs text-muted-foreground">
        {e.technicianName} · {when.toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })} · {EVIDENCE_PHASE_LABELS[e.phase]}
        {' · '}{e.jobName}
      </p>
      {e.kind === 'SESSION_CONCLUSION' && e.sessionDisposition && (
        <p className="font-medium" data-testid="evidence-session-conclusion">
          Their session concluded: {DIAGNOSTIC_COMPETENCY_LABELS[e.sessionDisposition]}
          {e.confidence !== null ? ` (confidence ${e.confidence}/5)` : ''} — not the final diagnosis
        </p>
      )}
      {e.tests.map((t, i) => (
        <p key={i}>
          <span className="font-medium">{t.testName}</span>
          {t.result ? `: ${t.result}` : ''}
          {t.values && typeof t.values === 'object' && 'reported' in (t.values as Record<string, unknown>)
            ? ` — read ${String((t.values as Record<string, unknown>).reported)}` : ''}
        </p>
      ))}
      {e.findings && <p><span className="font-medium">Found:</span> {e.findings}</p>}
      {e.reasoning && <p><span className="font-medium">Reasoning:</span> {e.reasoning}</p>}
      {e.unresolvedQuestions && <p><span className="font-medium">Unresolved:</span> {e.unresolvedQuestions}</p>}
      {e.recommendation && (
        <p data-testid="evidence-recommendation-entry">
          <span className="font-medium">Recommended repair:</span> {e.recommendation}{' '}
          <span className="text-xs text-muted-foreground">(recommendation — not an approved job)</span>
        </p>
      )}
    </div>
  );
}

/**
 * OCT28 #2: the technicians' findings and recommended repairs, read-only, for the front office (Command Center →
 * Workflow). The server's timeline is readable by every member of the ticket's shop; each entry keeps who / when.
 * A recommendation is shown as a recommendation — it never becomes, or reads as, an approved job.
 */
export function TechnicianFindingsPanel({ ticketId }: { ticketId: string }) {
  const [timeline, setTimeline] = useState<DiagnosticEvidenceTimeline | null>(null);
  useEffect(() => {
    let cancelled = false;
    void readDiagnosticEvidenceTimeline(ticketId).then((t) => { if (!cancelled) setTimeline(t); }).catch(() => {});
    return () => { cancelled = true; };
  }, [ticketId]);
  if (!timeline || (timeline.entries.length === 0 && timeline.jobs.every((j) => j.diagnosisOutcome === null))) return null;
  return (
    <section className="space-y-2" data-testid="technician-findings-panel" aria-label="Technician findings">
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Technician findings &amp; recommendations</h3>
      <DiagnosticEvidenceTimelineList timeline={timeline} />
    </section>
  );
}

/**
 * The "Findings" control beside a technician's own running / paused timer. Renders nothing unless the
 * server says this technician may add diagnostic evidence on this ticket right now.
 */
export function DiagnosticEvidenceButton({ ticketId, refreshKey }: { ticketId: string; refreshKey?: string }) {
  const [timeline, setTimeline] = useState<DiagnosticEvidenceTimeline | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formKey, setFormKey] = useState(0);
  // OCT28 #2: a failed save keeps the dialog open, the draft intact, and says what to do.
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setTimeline(await readDiagnosticEvidenceTimeline(ticketId));
  }, [ticketId]);
  useEffect(() => { void load(); }, [load, refreshKey]);

  const recordable = useMemo(
    () => (timeline?.jobs ?? []).filter((j) => timeline?.recordableJobIds.includes(j.jobId)),
    [timeline],
  );
  if (!timeline || recordable.length === 0) return null;

  const submit = async (jobId: string, draft: EvidenceDraft) => {
    setBusy(true);
    setSaveError(null);
    try {
      const r = await recordMyDiagnosticEvidence(ticketId, jobId, evidencePayload(draft), crypto.randomUUID());
      if (r.ok === true) {
        // OCT28 #2: saved on the server → the dialog closes; the next opening starts from an empty form.
        toast.success('Findings saved', { description: 'Added to the diagnosis record. The diagnosis stays open.' });
        setFormKey((k) => k + 1);
        setOpen(false);
      } else {
        const message = evidenceRefusalMessage(r.outcome, r.reason);
        setSaveError(message);
        toast.error(message);
      }
      await load().catch(() => {});
    } catch {
      const message = 'Your findings were not saved — check the connection and press Save findings again. Nothing you typed was lost.';
      setSaveError(message);
      toast.error(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="outline" className={cn(PHONE_TAP, 'gap-2')} data-testid="diagnostic-evidence-open"
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}>
        <Stethoscope className="h-4 w-4" />
        Findings
      </Button>
      <Dialog open={open} onOpenChange={(o) => { if (!busy) { setOpen(o); if (!o) setSaveError(null); } }}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="diagnostic-evidence-dialog"
                       onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle>Diagnostic findings</DialogTitle>
            <DialogDescription>
              Record what you have tested and found so far — any time while you work, while paused, or after you resume.
            </DialogDescription>
          </DialogHeader>
          {saveError && (
            <p role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-2 text-sm text-destructive"
               data-testid="evidence-save-error">{saveError}</p>
          )}
          <DiagnosticEvidenceForm key={formKey} jobs={recordable.map((j) => ({ jobId: j.jobId, jobName: j.jobName }))}
                                  busy={busy} onSubmit={(jobId, d) => { void submit(jobId, d); }} />
          <div className="border-t pt-3">
            <p className="mb-2 text-sm font-medium">Recorded so far</p>
            <DiagnosticEvidenceTimelineList timeline={timeline} />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
